// Native <audio> player that streams from a short-lived signed URL and
// survives TTL expiry. Two failure modes we defend against:
//
//   1) The student idles on the review page past the URL's TTL (max 5min)
//      and then clicks Play. Chrome silently 410s the token. We hook the
//      <audio> element's `error` event and refetch a fresh URL, then play.
//
//   2) A brief network hiccup during load. Same recovery path — refetch
//      once and try again. We cap the retry at 2 so a genuinely broken
//      backend can't loop forever.
//
// The backend now mints these URLs at SIGN_MAX_TTL (5 minutes), so a normal
// review session never trips the refetch path; this is the safety net.
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '@/lib/api';

const MAX_REFETCHES = 2;

export default function SignedAudioPlayer({ exam, submissionId, className, testId }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const audioRef = useRef(null);
  const refetchesRef = useRef(0);

  const fetchUrl = useCallback(async () => {
    try {
      const r = await api.speakingAudioSign(exam, submissionId);
      const next = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setUrl(next);
      setFailed(false);
      return next;
    } catch {
      setFailed(true);
      return null;
    }
  }, [exam, submissionId]);

  // Initial fetch. Reset on submissionId change (opening a different response
  // row must start from a clean URL + retry counter).
  useEffect(() => {
    refetchesRef.current = 0;
    setUrl(null);
    setFailed(false);
    fetchUrl();
  }, [fetchUrl]);

  // Recover from 410 / network drop by refetching a fresh signed URL.
  // We only run this while the element is present and the URL was set.
  const onError = useCallback(async () => {
    if (refetchesRef.current >= MAX_REFETCHES) { setFailed(true); return; }
    refetchesRef.current += 1;
    const next = await fetchUrl();
    if (!next) return;
    // Give React one frame to bind the new src before we try play again.
    setTimeout(() => {
      try { audioRef.current?.load(); audioRef.current?.play?.().catch(() => {}); }
      catch { /* ignore */ }
    }, 0);
  }, [fetchUrl]);

  if (failed) {
    return (
      <p className="mt-2 text-xs text-slate-500" data-testid={testId ? `${testId}-error` : undefined}>
        Couldn’t load this recording. Refresh the page to try again.
      </p>
    );
  }
  if (!url) {
    return <p className="mt-2 text-xs text-slate-400">Loading recording…</p>;
  }
  return (
    <audio
      ref={audioRef}
      controls
      src={url}
      onError={onError}
      className={className}
      data-testid={testId}
    />
  );
}
